/**
 * «Фибо-пульт»: сетку фибо рисует человек, сделку по ней ведёт бот.
 *
 * Владелец (21.10.2026) торгует по фибо в плюс, а механическая сетка
 * (выбор хая и лоя по правилу) перевеса не дала: дело в том, какой
 * диапазон человек считает главным. Поэтому уровни выбирает человек —
 * натягивает обычную «Коррекцию Фибоначчи», — а бот исполняет: лимитка,
 * стоп, цели, перенос стопа в безубыток, снятие заявки по сроку.
 *
 * Настройка сделки хранится в самой фигуре (`trade`), как оповещение на
 * рисунке: двинули сетку — сервер видит новые уровни; удалили или
 * выключили — заявка снимается. Расчёт плана — здесь, один на экран и
 * сервер: цифры на графике и на площадке не должны расходиться.
 *
 * Два режима:
 *   «от 1.414» (reversal) — цена продавила диапазон до продолжения,
 *     вход против движения на 1.414 (или зоной 1.414 + 1.618 по половине),
 *     стоп чуть за 1.618, цель — возврат к 1.0;
 *   «к 1.414» (trend) — цена пробила 1.0 и идёт к продолжению: вход на
 *     откате к 1.0 (или сразу на пробое), стоп внутри диапазона, цель 1.414.
 * «Держать» — половина закрывается на цели, стоп остатка в безубыток,
 * остаток держится дальше (разворот — до 0.5, по движению — до 1.618).
 */
export const FIBO_DEFAULTS = {
    reversal: { mode: 'reversal', entry: 1.414, trendEntry: 'retest', stop: 1.65, target: 1, hold: false, riskPercent: 0.5, ttlHours: 72 },
    trend: { mode: 'trend', entry: 1, trendEntry: 'retest', stop: 0.786, target: 1.414, hold: false, riskPercent: 0.5, ttlHours: 72 },
};
/** Выбор уровней в окне — те же числа проверяет сервер. */
export const FIBO_CHOICES = {
    reversal: { entry: [1.272, 1.414, 1.5, 1.618], stop: [1.65, 1.786, 2], target: [0.5, 0.618, 1, 1.2] },
    trend: { stop: [0.786, 0.618, 0.5], target: [1.272, 1.414, 1.618] },
    ttl: [24, 72, 168, null],
};
export const FIBO_RISK = { min: 0.05, max: 3 };
const num = (value) => (typeof value === 'number' && Number.isFinite(value) ? value : null);
/** Настройка из профиля: всё лишнее отброшено, неверное — `undefined`. */
export function normalizeFiboTrade(value) {
    if (typeof value !== 'object' || value === null)
        return undefined;
    const raw = value;
    const mode = raw.mode === 'trend' ? 'trend' : 'reversal';
    const base = FIBO_DEFAULTS[mode];
    const entry = num(raw.entry) ?? base.entry;
    const entry2 = num(raw.entry2);
    const stop = num(raw.stop) ?? base.stop;
    const target = num(raw.target) ?? base.target;
    const risk = num(raw.riskPercent) ?? base.riskPercent;
    const ttl = raw.ttlHours === null ? null : (num(raw.ttlHours) ?? base.ttlHours);
    return {
        enabled: raw.enabled === true,
        mode,
        account: typeof raw.account === 'string' && raw.account !== '' ? raw.account : null,
        entry,
        ...(entry2 !== null && mode === 'reversal' ? { entry2 } : {}),
        trendEntry: raw.trendEntry === 'break' ? 'break' : 'retest',
        stop,
        target,
        hold: raw.hold === true,
        riskPercent: Math.min(FIBO_RISK.max, Math.max(FIBO_RISK.min, risk)),
        ttlHours: ttl === null ? null : Math.max(1, ttl),
        since: num(raw.since) ?? 0,
    };
}
/** Цена уровня сетки: `from + (to − from) · v` — как рисует график. */
export function fiboLevelPrice(shape, value) {
    const first = shape.points[0]?.price;
    const second = shape.points[1]?.price;
    if (first === undefined || second === undefined || !(first !== second))
        return null;
    let [from, to] = shape.fib?.fromEnd === true ? [second, first] : [first, second];
    if (shape.fib?.reverse)
        [from, to] = [to, from];
    return from + (to - from) * value;
}
/**
 * План сделки по сетке. `null` — уровни не складываются в сделку (стоп не
 * по ту сторону входа, цель не по эту, сетка нулевой высоты).
 */
export function fiboPlan(shape, trade) {
    const level = (value) => fiboLevelPrice(shape, value);
    const one = level(1);
    const zero = level(0);
    const ext = level(1.414);
    if (one === null || zero === null || ext === null)
        return null;
    // Продолжение (1.414) ниже 1.0 — сетка «вниз»: разворот — лонг, по движению — шорт.
    const down = ext < one;
    if (trade.mode === 'reversal') {
        const side = down ? 'LONG' : 'SHORT';
        const first = level(trade.entry);
        const second = trade.entry2 !== undefined ? level(trade.entry2) : null;
        const stop = level(trade.stop);
        const target = level(trade.target);
        const rest = level(0.5);
        if (first === null || stop === null || target === null || rest === null)
            return null;
        const entries = second !== null ? [{ price: first, share: 0.5 }, { price: second, share: 0.5 }] : [{ price: first, share: 1 }];
        return finish(side, entries, false, stop, trade.hold ? [{ price: target, share: 0.5 }, { price: rest, share: 0.5 }] : [{ price: target, share: 1 }], trade.hold, null, target);
    }
    const side = down ? 'SHORT' : 'LONG';
    const stop = level(trade.stop);
    const target = level(trade.target);
    const further = level(1.618);
    if (stop === null || target === null || further === null)
        return null;
    // Пробой — цена прошла 1.0 на десятую часть высоты сетки: касание края — ещё не пробой.
    const trigger = one + (down ? -1 : 1) * 0.1 * Math.abs(one - zero);
    const entries = [{ price: trade.trendEntry === 'break' ? trigger : one, share: 1 }];
    return finish(side, entries, trade.trendEntry === 'break', stop, trade.hold ? [{ price: target, share: 0.5 }, { price: further, share: 0.5 }] : [{ price: target, share: 1 }], trade.hold, trigger, target);
}
function finish(side, entries, market, stop, targets, breakeven, trigger, cancelAt) {
    const sign = side === 'LONG' ? 1 : -1;
    const averageEntry = entries.reduce((sum, entry) => sum + entry.price * entry.share, 0);
    const risk = sign * (averageEntry - stop);
    if (!(risk > 0))
        return null;
    if (entries.some((entry) => !(sign * (entry.price - stop) > 0)))
        return null;
    if (targets.some((target) => !(sign * (target.price - averageEntry) > 0)))
        return null;
    const reward = targets.reduce((sum, target) => sum + sign * (target.price - averageEntry) * target.share, 0);
    return { side, entries, market, stop, targets, breakeven, trigger, cancelAt, averageEntry, rewardR: reward / risk };
}
//# sourceMappingURL=fibo-pult.js.map